// src/features/messages/MessagesPage.tsx — auth-gated messenger page.
import { useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useConversationPolling, useMessages, useSession, useViewport } from '@/hooks';
import { useRightRail, RailGroup, RailPanel } from '@/app/shell/RightRail';
import { Sheet } from '@/ui';
import { Icon } from '@/icons';
import { RailToolbarContext } from '@/app/shell/RightRail/context';
import { RailHeader } from '@/app/shell/RightRail/RailHeader';
import { RailHeaderButton } from '@/app/shell/RightRail/RailHeader.styles';
import { media } from '@/theme/media';
import { ConversationList } from './ConversationList';
import { ConversationThread } from './ConversationThread';
import { AboutChatPanel } from './panels/AboutChatPanel';
import { SharedMediaPanel } from './panels/SharedMediaPanel';
import { filterConversations } from './filterConversations';
import type { ConvoFilter } from './meta';
import type { OutgoingMessageImage } from '@/core/services/messagesService';

const Shell = styled.section`
  display: flex;
  min-height: 0;
  height: calc(100vh - 140px);
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};

  ${media.mobile`
    height: calc(100dvh - 120px);
    border-radius: 0;
    border-left: none;
    border-right: none;
  `}
`;

export interface MessagesPageProps {
  initialFilter?: ConvoFilter;
}

export function MessagesPage({ initialFilter = 'all' }: MessagesPageProps) {
  const { isAuthenticated, currentUser, isEmailVerified } = useSession();
  // `requestsCount` is derived in the hook (alongside totalUnread, which
  // excludes the same threads) rather than re-derived here — one rule, one
  // place, so the chip and the badge can never disagree.
  const { conversations, requestsCount, send, markRead, acceptRequest, declineRequest } =
    useMessages();
  // Keep the page live. The realtime socket (dm.message) is the primary path;
  // this poll is the fallback for when it is down — it skips every tick while
  // the socket is connected, and pauses while the tab is hidden.
  useConversationPolling();
  const viewport = useViewport();
  const isMobile = viewport === 'mobile';

  // A profile's Message button starts (or finds) the conversation with that person and
  // hands its id over in the navigation state, so the page opens ON that thread instead
  // of on whichever conversation happens to be first.
  const location = useLocation();
  const requested = (location.state as { openConversation?: string } | null)?.openConversation;
  const [activeConvoId, setActiveConvoId] = useState<string | null>(requested ?? null);
  const [filter, setFilter] = useState<ConvoFilter>(initialFilter);
  const [search, setSearch] = useState('');
  const [chatInfoOpen, setChatInfoOpen] = useState(false);
  const [infoToolbar, setInfoToolbar] = useState<HTMLSpanElement | null>(null);

  const visible = useMemo(
    () => filterConversations(conversations, { filter, search }),
    [conversations, filter, search],
  );

  // On desktop/tablet: derive active from explicit selection or first visible (auto-select).
  // On mobile: only use explicit selection (no auto-select — user must tap).
  const activeConvo = isMobile
    ? (visible.find((c) => c.id === activeConvoId) ?? null)
    : (visible.find((c) => c.id === activeConvoId) ?? visible[0] ?? null);

  // Desktop/tablet: panels in the right rail.
  // Mobile: panels in a bottom Sheet; right rail stays empty to avoid layout conflicts.
  const rightRail = useMemo(
    () =>
      !isMobile && activeConvo ? (
        <RailGroup pageKey="messages">
          <RailPanel id="about" label="About this chat">
            <AboutChatPanel convo={activeConvo} />
          </RailPanel>
          <RailPanel id="media" label="Shared media">
            <SharedMediaPanel convo={activeConvo} />
          </RailPanel>
        </RailGroup>
      ) : null,
    [isMobile, activeConvo],
  );
  useRightRail(rightRail);

  if (!isAuthenticated || !currentUser) {
    return <Navigate to={`/signin?next=${encodeURIComponent('/messages')}`} replace />;
  }

  function onSelect(id: string) {
    setActiveConvoId(id);
    // A message request is deliberately NOT marked read on open — it carries no
    // unread count until it's accepted, and telling the sender "read" before
    // the recipient agreed to the conversation would leak the very signal the
    // Requests tab exists to withhold.
    if (conversations.find((c) => c.id === id)?.pending) return;
    void markRead(id);
  }
  function onConversationCreated(id: string) {
    // A brand-new/just-accepted conversation starts unread:0 already
    // (the creator/acceptor is caught up by construction) — select it
    // directly, no markRead round-trip needed. Clear any active search FIRST:
    // filterConversations would otherwise hide the new thread behind a search
    // term that doesn't match it — desktop's auto-select fallback would then
    // land on an unrelated visible conversation, and mobile would show nothing.
    setSearch('');
    setActiveConvoId(id);
  }
  function onBack() {
    setActiveConvoId(null);
    setChatInfoOpen(false);
  }
  // Returns the promise so the composer/request bar can report a failure
  // instead of the message or the decision vanishing silently.
  function onSend(text: string, image?: OutgoingMessageImage): Promise<void> {
    if (!activeConvo) return Promise.resolve();
    // Two call shapes on purpose: a plain text send must keep passing exactly
    // two arguments so the wire payload stays `{ body }`.
    return image ? send(activeConvo.id, text, image) : send(activeConvo.id, text);
  }
  async function onAcceptRequest(id: string) {
    await acceptRequest(id);
    // Accepting moves the thread out of Requests; follow it into the inbox
    // rather than leaving the user staring at an empty Requests tab.
    setFilter('all');
    setActiveConvoId(id);
  }
  async function onDeclineRequest(id: string) {
    await declineRequest(id);
    if (activeConvoId === id) setActiveConvoId(null);
  }

  // Mobile drill-down: show conversation list OR thread, never both simultaneously.
  if (isMobile) {
    const showThread = activeConvo !== null;
    return (
      <>
        <Shell>
          {!showThread && (
            <ConversationList
              conversations={visible}
              activeId={null}
              onSelect={onSelect}
              filter={filter}
              onFilterChange={setFilter}
              search={search}
              onSearchChange={setSearch}
              onConversationCreated={onConversationCreated}
              requestsCount={requestsCount}
            />
          )}
          {showThread && (
            <ConversationThread
              convo={activeConvo}
              onSend={onSend}
              onBack={onBack}
              onChatInfo={() => setChatInfoOpen(true)}
              onAccept={onAcceptRequest}
              onDecline={onDeclineRequest}
              emailVerified={isEmailVerified}
            />
          )}
        </Shell>
        {activeConvo && (
          <Sheet
            side="bottom"
            open={chatInfoOpen}
            onClose={() => setChatInfoOpen(false)}
            title="About this chat"
            renderHeader={(titleId) => (
              <RailHeader title="About this chat" titleId={titleId}>
                <span ref={setInfoToolbar} />
                <RailHeaderButton
                  type="button"
                  aria-label="Close chat information"
                  title="Close chat information"
                  onClick={() => setChatInfoOpen(false)}
                >
                  <Icon name="close" size={18} />
                </RailHeaderButton>
              </RailHeader>
            )}
          >
            <RailToolbarContext.Provider value={infoToolbar}>
              <RailGroup pageKey="messages">
                <RailPanel id="about" label="About this chat">
                  <AboutChatPanel convo={activeConvo} />
                </RailPanel>
                <RailPanel id="media" label="Shared media">
                  <SharedMediaPanel convo={activeConvo} />
                </RailPanel>
              </RailGroup>
            </RailToolbarContext.Provider>
          </Sheet>
        )}
      </>
    );
  }

  // Desktop / tablet: original 2-pane layout (unchanged).
  return (
    <Shell>
      <ConversationList
        conversations={visible}
        activeId={activeConvo?.id ?? null}
        onSelect={onSelect}
        filter={filter}
        onFilterChange={setFilter}
        search={search}
        onSearchChange={setSearch}
        onConversationCreated={onConversationCreated}
        requestsCount={requestsCount}
      />
      <ConversationThread
        convo={activeConvo}
        onSend={onSend}
        onAccept={onAcceptRequest}
        onDecline={onDeclineRequest}
        emailVerified={isEmailVerified}
      />
    </Shell>
  );
}
